import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormLabel,
  Paper,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  getRecommendations,
  addRecommendations,
  updateRecommendation,
  deleteRecommendation,
} from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import RichtextEditor from '../../../common/RichtextEditor';

export default function ResultRecommendations({
  facilityId,
  quarter,
  year,
  showFacilityName = false,
  showActions = false,
}) {
  const [recommendationList, setRecommendationList] = useState([]);
  const [recommendationLoading, setRecommendationLoading] = useState(false);

  // Add/Edit Dialog States
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [newActions, setNewActions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Delete Confirmation Dialog States
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (facilityId && quarter && year) {
      fetchRecommendations();
    }
  }, [facilityId, quarter, year]);

  const fetchRecommendations = async () => {
    setRecommendationLoading(true);
    try {
      const res = await getRecommendations(facilityId, quarter, year);
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setRecommendationList(data || []);
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setRecommendationLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setEditingItem(null);
    setNewComment('');
    setNewActions('');
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (item) => {
    setEditingItem(item);
    setNewComment(item.comment || '');
    setNewActions(item.actions || '');
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingItem(null);
    setNewComment('');
    setNewActions('');
  };

  const handleOpenDeleteDialog = (item) => {
    setDeletingItem(item);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeletingItem(null);
  };

  const isContentEmpty = (htmlString) => {
    if (!htmlString) return true;
    const plainText = htmlString.replace(/<[^>]*>/g, '').trim();
    return !plainText;
  };

  const handleSubmit = async () => {
    if (isContentEmpty(newComment)) {
      openNotification({ type: 'error', message: 'Please fill in the Comment field.' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        facilityId,
        reportQuarter: quarter,
        reportYear: year,
        comment: newComment,
        actions: newActions,
      };

      let res;
      if (editingItem) {
        res = await updateRecommendation({ ...payload, id: editingItem.id });
      } else {
        res = await addRecommendations(payload);
      }

      const { status } = res?.result || {};

      if (status?.code === '00') {
        openNotification({
          type: 'success',
          message:
            status?.desc || `Recommendation ${editingItem ? 'updated' : 'added'} successfully`,
        });
        handleCloseDialog();
        fetchRecommendations();
      } else {
        openNotification({
          type: 'error',
          message: status?.desc || `Failed to ${editingItem ? 'update' : 'add'} recommendation`,
        });
      }
    } catch (error) {
      console.error('Error saving recommendation:', error);
      openNotification({
        type: 'error',
        message: 'An error occurred while saving. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem) return;

    setDeleting(true);
    try {
      const res = await deleteRecommendation(deletingItem.id);
      const { status } = res?.result || {};

      if (status?.code === '00') {
        openNotification({
          type: 'success',
          message: status?.desc || 'Recommendation deleted successfully',
        });
        handleCloseDeleteDialog();
        fetchRecommendations();
      } else {
        openNotification({
          type: 'error',
          message: status?.desc || 'Failed to delete recommendation',
        });
      }
    } catch (error) {
      console.error('Error deleting recommendation:', error);
      openNotification({
        type: 'error',
        message: 'Failed to delete recommendation. Please try again.',
      });
    } finally {
      setDeleting(false);
    }
  };

  const isFormInvalid = isContentEmpty(newComment);

  return (
    <Box sx={{ width: '100%', my: 2 }}>
      {/* Header Container */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="h2" fontWeight="700" color="text.primary">
          Recommendations
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            py: 1,
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.35)',
            },
          }}
        >
          Add Recommendation
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Recommendations List View */}
      {recommendationLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={36} />
        </Box>
      ) : recommendationList.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
            backgroundColor: '#fafafa',
            borderStyle: 'dashed',
          }}
        >
          <Typography color="text.secondary" fontWeight="500">
            No recommendations available.
          </Typography>
          <Typography color="text.disabled" variant="body2" mt={0.5}>
            Click "Add Recommendation" to create the first one.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          {recommendationList.map((item) => (
            <Card
              key={item.id}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Stack spacing={2}>
                  {/* Top Bar */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    {showFacilityName ? (
                      <Typography
                        variant="h6"
                        component="div"
                        fontWeight="600"
                        color="text.primary"
                      >
                        {item.facilityName}
                      </Typography>
                    ) : (
                      <Box />
                    )}

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={`Q${item.reportQuarter} ${item.reportYear}`}
                        size="small"
                        color="primary"
                        sx={{
                          fontWeight: 600,
                          borderRadius: '6px',
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          color: 'primary.main',
                          border: '1px solid rgba(25, 118, 210, 0.2)',
                        }}
                      />

                      {showActions && (
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Edit Recommendation">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditDialog(item)}
                              sx={{
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.12)' },
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Recommendation">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(item)}
                              sx={{
                                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.12)' },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Comment Section */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontWeight: 700,
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Comment
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      component="div"
                      sx={{
                        '& p': { m: 0, lineHeight: 1.6 },
                        p: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: '#f8f9fa',
                        borderLeft: '3px solid #1976d2',
                      }}
                      dangerouslySetInnerHTML={{ __html: item.comment }}
                    />
                  </Box>

                  {/* Actions Section */}
                  {item.actions && !isContentEmpty(item.actions) && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 700,
                          color: 'text.secondary',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Actions
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        component="div"
                        sx={{
                          '& p': { m: 0, lineHeight: 1.6 },
                          p: 1.5,
                          borderRadius: 1.5,
                          backgroundColor: '#f8f9fa',
                          borderLeft: '3px solid #2e7d32',
                        }}
                        dangerouslySetInnerHTML={{ __html: item.actions }}
                      />
                    </Box>
                  )}

                  {/* Footer Metadata Section */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    pt={1}
                    sx={{ borderTop: '1px solid', borderColor: 'grey.100' }}
                  >
                    {item.updateDate ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">
                          Last updated by {item.updatedByFullName} on{' '}
                          {new Intl.DateTimeFormat('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }).format(new Date(item.updateDate))}
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                          Submitted by:
                        </Typography>
                        <Typography variant="caption" color="text.primary" fontWeight="600">
                          {item.fullName}
                        </Typography>
                        {item.emailAddress && (
                          <Typography variant="caption" color="text.secondary">
                            ({item.emailAddress})
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Add / Edit Recommendation Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
        aria-labelledby="add-edit-recommendation-dialog-title"
      >
        <DialogTitle id="add-edit-recommendation-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="700">
              {editingItem ? 'Edit Recommendation' : 'Add New Recommendation'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small" sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: 'divider' }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <Box display="flex" flexDirection="column" gap={1}>
              <FormLabel
                required
                sx={{ fontWeight: '600', color: 'text.primary', fontSize: '0.9rem' }}
              >
                Comment
              </FormLabel>
              <RichtextEditor value={newComment} onChange={setNewComment} />
              <Typography variant="caption" color="text.secondary">
                Describe the observation or issue.
              </Typography>
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
              <FormLabel sx={{ fontWeight: '600', color: 'text.primary', fontSize: '0.9rem' }}>
                Actions
              </FormLabel>
              <RichtextEditor value={newActions} onChange={setNewActions} />
              <Typography variant="caption" color="text.secondary">
                Outline the recommended actions or next steps.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={handleCloseDialog}
            color="inherit"
            sx={{ textTransform: 'none', borderRadius: 2, px: 2.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={submitting || isFormInvalid}
            startIcon={submitting && <CircularProgress size={18} color="inherit" />}
            sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
          >
            {submitting
              ? 'Saving...'
              : editingItem
              ? 'Update Recommendation'
              : 'Add Recommendation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2.5 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this recommendation? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            color="inherit"
            sx={{ textTransform: 'none', borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting && <CircularProgress size={16} color="inherit" />}
            sx={{ textTransform: 'none', borderRadius: 1.5 }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
